import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * useWakeLock - Hook para mantener la pantalla encendida
 * 
 * Utiliza la Screen Wake Lock API para evitar que el dispositivo se duerma
 * mientras la aplicación está en estado crítico (ej: gestión de torneo en vivo).
 */
export const useWakeLock = () => {
    const wakeLock = useRef(null);
    const [isLocked, setIsLocked] = useState(false);

    const requestWakeLock = useCallback(async () => {
        if ('wakeLock' in navigator) {
            try {
                wakeLock.current = await navigator.wakeLock.request('screen');
                wakeLock.current.addEventListener('release', () => {
                    console.log('Wake Lock released');
                    setIsLocked(false);
                });
                console.log('Wake Lock active');
                setIsLocked(true);
            } catch (err) {
                console.error(`${err.name}, ${err.message}`);
            }
        } else {
            console.warn("Wake Lock API not supported in this browser.");
        }
    }, []);

    const releaseWakeLock = useCallback(async () => {
        if (wakeLock.current) {
            await wakeLock.current.release();
            wakeLock.current = null;
            setIsLocked(false);
        }
    }, []);

    // Re-solicitar el bloqueo si la visibilidad del documento cambia (ej: cambiar de pestaña y volver)
    useEffect(() => {
        const handleVisibilityChange = async () => {
            if (wakeLock.current !== null && document.visibilityState === 'visible') {
                await requestWakeLock();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [requestWakeLock]);

    // Limpieza al desmontar
    useEffect(() => {
        return () => {
            releaseWakeLock();
        };
    }, [releaseWakeLock]);

    return { isLocked, requestWakeLock, releaseWakeLock };
};

export default useWakeLock;
